const MODE_KEY = 'workloop.workMode';
const SPEAK_OFF_KEY = 'workloop.wfhSpeechOff';
const EVENT_NAME = 'workloop:work-mode-changed';
const USER_STORAGE_KEY = 'workloop_user';

export const WORK_MODE_OFFICE = 'office';
export const WORK_MODE_WFH = 'wfh';

let modeCache = null;
let speakOffCache = null;
let liveStatus = null;
let liveLocation = null;

const readCachedUser = () => {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const isLoggedIn = () => {
  try {
    return Boolean(localStorage.getItem('token') && readCachedUser()?.id);
  } catch {
    return false;
  }
};

const userStatus = () => String(liveStatus || readCachedUser()?.status || '').toUpperCase();
const userLocation = () => String(liveLocation || readCachedUser()?.workLocation || '').toUpperCase();

const isWorkFromHomeMode = () =>
  userLocation() === 'WORK_FROM_HOME' || userStatus() === 'WORK_FROM_HOME';

const readMode = () => {
  if (isWorkFromHomeMode()) return WORK_MODE_WFH;
  if (userLocation() === 'WORK_FROM_OFFICE' || userStatus() === 'WORK_FROM_OFFICE') return WORK_MODE_OFFICE;
  if (modeCache !== null) return modeCache;
  try {
    const stored = localStorage.getItem(MODE_KEY);
    modeCache = stored === WORK_MODE_WFH ? WORK_MODE_WFH : WORK_MODE_OFFICE;
  } catch {
    modeCache = WORK_MODE_OFFICE;
  }
  return modeCache;
};

const isSpeechExplicitlyOff = () => {
  if (speakOffCache !== null) return speakOffCache;
  try {
    speakOffCache = localStorage.getItem(SPEAK_OFF_KEY) === 'true';
  } catch {
    speakOffCache = false;
  }
  return speakOffCache;
};

const writeMode = (mode) => {
  modeCache = mode === WORK_MODE_WFH ? WORK_MODE_WFH : WORK_MODE_OFFICE;
  try {
    localStorage.setItem(MODE_KEY, modeCache);
  } catch {
    // ignore quota
  }
  notifyChanged();
};

const writeSpeechOff = (off) => {
  speakOffCache = Boolean(off);
  try {
    if (speakOffCache) {
      localStorage.setItem(SPEAK_OFF_KEY, 'true');
    } else {
      localStorage.removeItem(SPEAK_OFF_KEY);
    }
  } catch {
    // ignore quota
  }
  notifyChanged();
};

const notifyChanged = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  }
};

export const getWorkMode = () => readMode();

export const isWorkFromHome = () => isWorkFromHomeMode() || readMode() === WORK_MODE_WFH;

export const getSpeechRequirements = () => ({
  loggedIn: isLoggedIn(),
  workFromHome: isWorkFromHomeMode(),
});

export const isNotificationSpeechEnabled = () => {
  const { loggedIn, workFromHome } = getSpeechRequirements();
  if (userStatus() === 'IN_A_CALL') return false;
  return loggedIn && workFromHome && !isSpeechExplicitlyOff();
};

export const setWorkMode = (mode) => {
  writeMode(mode);
  return readMode();
};

export const setNotificationSpeechEnabled = (enabled) => {
  writeSpeechOff(!enabled);
  return isNotificationSpeechEnabled();
};

export const syncWorkModeFromStatus = (status, workLocation) => {
  const value = String(status || '').toUpperCase();
  const location = String(workLocation || '').toUpperCase();
  if (value) liveStatus = value;
  if (location) liveLocation = location;
  const resolvedLocation = location
    || (value === 'WORK_FROM_HOME' || value === 'WORK_FROM_OFFICE' ? value : liveLocation);
  if (resolvedLocation === 'WORK_FROM_HOME' || value === 'WORK_FROM_HOME') {
    liveLocation = 'WORK_FROM_HOME';
    modeCache = WORK_MODE_WFH;
    try {
      localStorage.setItem(MODE_KEY, WORK_MODE_WFH);
    } catch {
      // ignore
    }
    notifyChanged();
  } else if (resolvedLocation === 'WORK_FROM_OFFICE' || value === 'WORK_FROM_OFFICE') {
    liveLocation = 'WORK_FROM_OFFICE';
    modeCache = WORK_MODE_OFFICE;
    try {
      localStorage.setItem(MODE_KEY, WORK_MODE_OFFICE);
    } catch {
      // ignore
    }
    notifyChanged();
  }
};

export const subscribeWorkModeChanged = (callback) => {
  if (typeof window === 'undefined') return () => {};
  const handler = () => callback();
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
};
